import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    getSupabaseClient,
    hasSupabaseConfig,
} from "../services/supabaseClient";
import "../styles/session.css";
import {
    isAwaitingPasswordRecovery,
    isPasswordRecoverySession,
    trackPasswordRecovery,
} from "../services/passwordRecovery";
import { buildPublicAppUrl } from "../services/publicAppUrl";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const SESSION_TIMEOUT_MINUTES = toNumber(
    process.env.REACT_APP_SESSION_TIMEOUT_MINUTES ||
        process.env.SESSION_TIMEOUT_MINUTES,
    30
);
const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;
const WARNING_BEFORE_MS = 5 * 60 * 1000;
const WARNING_GRACE_MS = 30 * 1000;
const ACTIVITY_THROTTLE_MS = 60 * 1000;
const SESSION_STORAGE_KEY = "pm_session_id";

export const INTERNAL_DOMAINS = [
    "rams-app.com",
    "be-accredited.com",
    "beaccredited.com",
];

export function deriveRoleFromEmail(email) {
    const domain = (email || "").split("@")[1]?.toLowerCase();
    if (!domain) return "client";
    return INTERNAL_DOMAINS.includes(domain) ? "internal_staff" : "client";
}

const resolveUserRole = (user, profile) => {
    if (profile?.role) {
        return profile.role;
    }
    const metadataRole = user?.user_metadata?.role;
    if (metadataRole) {
        return metadataRole;
    }
    return deriveRoleFromEmail(user?.email || "");
};

const AuthContext = createContext({
    authEnabled: hasSupabaseConfig,
    user: null,
    profile: null,
    role: null,
    roleLoading: false,
    session: null,
    recoveryUser: null,
    loading: false,
    error: null,
    emailVerificationPending: false,
    pendingVerificationEmail: null,
    currentSessionId: null,
    lastActivityTime: null,
    showTimeoutWarning: false,
    signIn: async () => {},
    completeSignIn: async () => {},
    signOut: async () => {},
    resendVerificationEmail: async () => {},
    staySignedIn: () => {},
    setError: () => {},
});

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const getDeviceInfo = () => {
    if (typeof navigator === "undefined") {
        return null;
    }

    const platform =
        navigator.userAgentData?.platform || navigator.platform || "";
    const vendor = navigator.vendor || "";
    const fallback = navigator.userAgent || "";
    const parts = [platform, vendor].filter(Boolean);
    if (parts.length > 0) {
        return parts.join(" ");
    }

    return fallback || null;
};

export function AuthProvider({ children }) {
    const [recoveryUser, setRecoveryUser] = useState(null);
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [role, setRole] = useState(null);
    const [roleLoading, setRoleLoading] = useState(false);
    const [loading, setLoading] = useState(hasSupabaseConfig);
    const [error, setError] = useState(null);
    const [emailVerificationPending, setEmailVerificationPending] = useState(false);
    const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);
    const [currentSessionId, setCurrentSessionId] = useState(() => {
        if (typeof window === "undefined") {
            return null;
        }
        return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    });
    const [lastActivityTime, setLastActivityTime] = useState(null);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

    const suppressSessionUpdatesRef = useRef(false);
    const pendingLoginEmailRef = useRef(null);
    const sessionTimeoutId = useRef(null);
    const warningTimeoutId = useRef(null);
    const warningGraceId = useRef(null);
    const activityUpdateRef = useRef(0);
    const sessionTokenRef = useRef(null);
    const sessionCreateRef = useRef(null);

    const clearSessionTimeouts = useCallback(() => {
        if (sessionTimeoutId.current) {
            clearTimeout(sessionTimeoutId.current);
            sessionTimeoutId.current = null;
        }
        if (warningTimeoutId.current) {
            clearTimeout(warningTimeoutId.current);
            warningTimeoutId.current = null;
        }
        if (warningGraceId.current) {
            clearTimeout(warningGraceId.current);
            warningGraceId.current = null;
        }
    }, []);

    const clearSessionId = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
        setCurrentSessionId(null);
        sessionTokenRef.current = null;
    }, []);

    const storeSessionId = useCallback((sessionId, accessToken) => {
        if (typeof window === "undefined") {
            return;
        }
        if (!sessionId) {
            return;
        }
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        setCurrentSessionId(sessionId);
        sessionTokenRef.current = accessToken || null;
    }, []);

    const checkLockout = useCallback(async (email) => {
        const response = await fetch(`${API_BASE_URL}/api/auth/check-lockout`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
        });

        if (response.status === 429) {
            const payload = await response.json().catch(() => ({}));
            const err = new Error(payload?.error || "Account is temporarily locked.");
            err.status = 429;
            err.remainingSeconds = payload?.remainingSeconds || 0;
            err.lockoutUntil = payload?.lockoutUntil || null;
            throw err;
        }

        if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload?.error || "Unable to check lockout status.");
        }

        return true;
    }, []);

    const recordLoginAttempt = useCallback(async ({ email, success }) => {
        await fetch(`${API_BASE_URL}/api/auth/record-attempt`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, success }),
        });
    }, []);

    const createSessionRecord = useCallback(
        async (sessionToRecord) => {
            if (!sessionToRecord?.access_token) {
                return;
            }

            if (
                sessionTokenRef.current &&
                sessionTokenRef.current === sessionToRecord.access_token &&
                currentSessionId
            ) {
                return;
            }

            if (sessionCreateRef.current === sessionToRecord.access_token) {
                return;
            }

            sessionCreateRef.current = sessionToRecord.access_token;
            try {
                const response = await fetch(`${API_BASE_URL}/api/sessions`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionToRecord.access_token}`,
                    },
                    body: JSON.stringify({
                        deviceInfo: getDeviceInfo(),
                    }),
                });

                if (!response.ok) {
                    return;
                }

                const payload = await response.json().catch(() => ({}));
                if (payload?.sessionId) {
                    storeSessionId(payload.sessionId, sessionToRecord.access_token);
                }
            } finally {
                if (sessionCreateRef.current === sessionToRecord.access_token) {
                    sessionCreateRef.current = null;
                }
            }
        },
        [currentSessionId, storeSessionId]
    );

    const touchSessionActivity = useCallback(async () => {
        if (!currentSessionId || !session?.access_token) {
            return;
        }

        const now = Date.now();
        if (now - activityUpdateRef.current < ACTIVITY_THROTTLE_MS) {
            return;
        }

        activityUpdateRef.current = now;

        await fetch(`${API_BASE_URL}/api/sessions/activity`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ sessionId: currentSessionId }),
        });
    }, [currentSessionId, session]);

    const signOut = useCallback(async () => {
        clearSessionTimeouts();
        setShowTimeoutWarning(false);
        setLastActivityTime(null);
        setEmailVerificationPending(false);
        setPendingVerificationEmail(null);
        suppressSessionUpdatesRef.current = false;
        pendingLoginEmailRef.current = null;
        setProfile(null);
        setRole(null);
        setRoleLoading(false);

        if (!hasSupabaseConfig) {
            clearSessionId();
            return;
        }

        const token = session?.access_token;
        if (token && currentSessionId) {
            await fetch(`${API_BASE_URL}/api/sessions/${currentSessionId}/revoke`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).catch(() => {});
        }

        const supabase = getSupabaseClient();
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) {
            setError(signOutError.message);
            throw signOutError;
        }
        setSession(null);
        setUser(null);
        trackPasswordRecovery("SIGNED_OUT", null);
        setRecoveryUser(null);
        setError(null);
        clearSessionId();
    }, [clearSessionId, clearSessionTimeouts, currentSessionId, session]);

    const resetSessionTimeout = useCallback(() => {
        if (!session) {
            return;
        }

        clearSessionTimeouts();
        setShowTimeoutWarning(false);
        setLastActivityTime(Date.now());

        const warningDelay = Math.max(SESSION_TIMEOUT_MS - WARNING_BEFORE_MS, 0);
        warningTimeoutId.current = setTimeout(() => {
            setShowTimeoutWarning(true);
        }, warningDelay);

        sessionTimeoutId.current = setTimeout(() => {
            setShowTimeoutWarning(true);
            warningGraceId.current = setTimeout(() => {
                signOut().catch(() => {});
            }, WARNING_GRACE_MS);
        }, SESSION_TIMEOUT_MS);

        touchSessionActivity().catch(() => {});
    }, [clearSessionTimeouts, session, signOut, touchSessionActivity]);

    const staySignedIn = useCallback(() => {
        resetSessionTimeout();
    }, [resetSessionTimeout]);

    useEffect(() => {
        if (!hasSupabaseConfig) {
            setLoading(false);
            return;
        }

        const supabase = getSupabaseClient();

        const applySession = (nextSession) => {
            const recovering = isPasswordRecoverySession(nextSession);
            setRecoveryUser(recovering ? nextSession.user : null);
            // Recovery must not load vault data or bypass the normal MFA sign-in flow.
            const loginSession = recovering || isAwaitingPasswordRecovery() ? null : nextSession;
            setSession(loginSession ?? null);
            setUser(loginSession?.user ?? null);
        };

        supabase.auth
            .getSession()
            .then(({ data, error: sessionError }) => {
                if (sessionError) {
                    setError(sessionError.message);
                    return;
                }
                if (suppressSessionUpdatesRef.current) {
                    return;
                }
                applySession(data.session);
            })
            .finally(() => setLoading(false));

        const { data: listener } = supabase.auth.onAuthStateChange(
            (event, sessionChange) => {
                trackPasswordRecovery(event, sessionChange);
                if (event === "PASSWORD_RECOVERY") {
                    suppressSessionUpdatesRef.current = false;
                }
                if (suppressSessionUpdatesRef.current) {
                    return;
                }
                applySession(sessionChange);
            }
        );

        return () => {
            listener?.subscription?.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!hasSupabaseConfig || !user?.id) {
            setProfile(null);
            setRole(null);
            setRoleLoading(false);
            return;
        }

        let active = true;
        setRoleLoading(true);
        const supabase = getSupabaseClient();

        supabase
            .from("profiles")
            .select("id, role, email, full_name, mfa_enabled")
            .eq("id", user.id)
            .maybeSingle()
            .then(({ data, error: profileError }) => {
                if (!active) {
                    return;
                }
                if (profileError) {
                    setProfile(null);
                    setRole(resolveUserRole(user, null));
                    return;
                }
                setProfile(data || null);
                setRole(resolveUserRole(user, data || null));
            })
            .catch(() => {
                if (!active) {
                    return;
                }
                setProfile(null);
                setRole(resolveUserRole(user, null));
            })
            .finally(() => {
                if (active) {
                    setRoleLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [user]);

    useEffect(() => {
        if (!session || !user) {
            clearSessionTimeouts();
            setShowTimeoutWarning(false);
            return;
        }

        const events = ["mousemove", "keydown", "click", "scroll"];
        const handleActivity = () => {
            resetSessionTimeout();
        };

        events.forEach((event) => window.addEventListener(event, handleActivity));
        resetSessionTimeout();

        return () => {
            events.forEach((event) =>
                window.removeEventListener(event, handleActivity)
            );
            clearSessionTimeouts();
        };
    }, [clearSessionTimeouts, resetSessionTimeout, session, user]);

    useEffect(() => {
        if (!session || !user) {
            return;
        }

        createSessionRecord(session).catch(() => {});
    }, [createSessionRecord, session, user]);

    const signIn = useCallback(
        async ({ email, password }) => {
            if (!hasSupabaseConfig) {
                throw new Error(
                    "Supabase auth is not configured. Set REACT_APP_SUPABASE_URL/REACT_APP_SUPABASE_ANON_KEY (or NEXT_PUBLIC_SUPABASE_URL/NEXT_PUBLIC_SUPABASE_ANON_KEY)."
                );
            }
            setError(null);

            const normalizedEmail = normalizeEmail(email);
            await checkLockout(normalizedEmail);

            const supabase = getSupabaseClient();
            suppressSessionUpdatesRef.current = true;
            pendingLoginEmailRef.current = normalizedEmail;

            const { data, error: authError } = await supabase.auth.signInWithPassword({
                email: normalizedEmail,
                password,
            });
            if (authError) {
                suppressSessionUpdatesRef.current = false;
                pendingLoginEmailRef.current = null;
                await recordLoginAttempt({
                    email: normalizedEmail,
                    success: false,
                }).catch(() => {});
                setError(authError.message);
                throw authError;
            }

            return data;
        },
        [checkLockout, recordLoginAttempt]
    );

    const completeSignIn = useCallback(
        async ({ session: nextSession, user: nextUser, email }) => {
            const resolvedEmail = normalizeEmail(
                email || nextUser?.email || pendingLoginEmailRef.current
            );

            suppressSessionUpdatesRef.current = false;
            pendingLoginEmailRef.current = null;

            // An explicit, completed login ends any previous recovery UI state.
            trackPasswordRecovery("SIGNED_OUT", null);
            setRecoveryUser(null);
            setSession(nextSession ?? null);
            setUser(nextUser ?? null);
            setRole(resolveUserRole(nextUser, null));
            setEmailVerificationPending(false);
            setPendingVerificationEmail(null);

            if (resolvedEmail) {
                await recordLoginAttempt({
                    email: resolvedEmail,
                    success: true,
                }).catch(() => {});
            }

            if (nextSession) {
                await createSessionRecord(nextSession).catch(() => {});
            }

            return nextSession;
        },
        [createSessionRecord, recordLoginAttempt]
    );

    const resendVerificationEmail = useCallback(
        async (targetEmail) => {
            if (!hasSupabaseConfig) {
                throw new Error(
                    "Supabase auth is not configured. Set REACT_APP_SUPABASE_URL/REACT_APP_SUPABASE_ANON_KEY (or NEXT_PUBLIC_SUPABASE_URL/NEXT_PUBLIC_SUPABASE_ANON_KEY)."
                );
            }

            const email =
                normalizeEmail(targetEmail) ||
                normalizeEmail(pendingVerificationEmail) ||
                normalizeEmail(user?.email);

            if (!email) {
                throw new Error("Email is required to resend verification.");
            }

            const supabase = getSupabaseClient();
            const { error: resendError } = await supabase.auth.resend({
                type: "signup",
                email,
                options: {
                    emailRedirectTo: buildPublicAppUrl(),
                },
            });

            if (resendError) {
                setError(resendError.message);
                throw resendError;
            }

            return true;
        },
        [pendingVerificationEmail, user]
    );

    const value = useMemo(
        () => ({
            authEnabled: hasSupabaseConfig,
            recoveryUser,
            session,
            user,
            profile,
            role,
            roleLoading,
            loading,
            error,
            emailVerificationPending,
            pendingVerificationEmail,
            currentSessionId,
            lastActivityTime,
            showTimeoutWarning,
            signIn,
            completeSignIn,
            signOut,
            resendVerificationEmail,
            staySignedIn,
            setError,
        }),
        [
            recoveryUser,
            session,
            user,
            profile,
            role,
            roleLoading,
            loading,
            error,
            emailVerificationPending,
            pendingVerificationEmail,
            currentSessionId,
            lastActivityTime,
            showTimeoutWarning,
            signIn,
            completeSignIn,
            signOut,
            resendVerificationEmail,
            staySignedIn,
        ]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
            {showTimeoutWarning && session && (
                <div className="session-timeout-modal">
                    <div className="modal-overlay" />
                    <div className="modal-card">
                        <div className="modal-header">
                            <h2>Session expiring soon</h2>
                        </div>
                        <p className="modal-body">
                            Your session will end soon due to inactivity. Stay signed in to
                            keep working.
                        </p>
                        <div className="modal-actions">
                            <button
                                className="btn primary"
                                type="button"
                                onClick={staySignedIn}
                            >
                                Stay signed in
                            </button>
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={() => signOut().catch(() => {})}
                            >
                                Sign out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

export default AuthContext;
